/**
 * Minimal hash router + route registry for the web app shell.
 *
 * No react-router: routes are `#/wiki`, `#/search`, `#/sources`,
 * `#/settings` (admin lives in the Settings screen's admin-only tab;
 * `#/admin` deep-links alias to settings). The shell reads ROUTES to
 * render the sidebar and lazy-loads the active screen. Each screen module
 * at web/screens/{wiki,search,sources,settings}.tsx default-exports a
 * React component taking no required props (see web/README-UI.md for the
 * screen contract).
 */
import {
  lazy,
  useCallback,
  useEffect,
  useState,
  type ComponentType,
  type LazyExoticComponent,
} from "react"
import {
  BookOpen,
  ClipboardCheck,
  FolderUp,
  MessageSquare,
  Network,
  Search,
  Settings,
  type LucideIcon,
} from "lucide-react"

export type RouteId = "wiki" | "search" | "graph" | "chat" | "sources" | "review" | "settings"

export interface RouteDef {
  id: RouteId
  /** Hash path, e.g. "#/wiki". */
  hash: string
  /** Sidebar + topbar label (sentence case). */
  label: string
  icon: LucideIcon
  /** Only shown/reachable when user.role === "admin". */
  adminOnly?: boolean
  component: LazyExoticComponent<ComponentType>
}

export const ROUTES: RouteDef[] = [
  {
    id: "wiki",
    hash: "#/wiki",
    label: "Wiki",
    icon: BookOpen,
    component: lazy(() => import("./screens/wiki")),
  },
  {
    id: "chat",
    hash: "#/chat",
    label: "Chat",
    icon: MessageSquare,
    component: lazy(() => import("./screens/chat")),
  },
  {
    id: "search",
    hash: "#/search",
    label: "Search",
    icon: Search,
    component: lazy(() => import("./screens/search")),
  },
  {
    id: "graph",
    hash: "#/graph",
    label: "Graph",
    icon: Network,
    component: lazy(() => import("./screens/graph")),
  },
  {
    id: "sources",
    hash: "#/sources",
    label: "Sources",
    icon: FolderUp,
    component: lazy(() => import("./screens/sources")),
  },
  {
    id: "review",
    hash: "#/review",
    label: "Review",
    icon: ClipboardCheck,
    component: lazy(() => import("./screens/review")),
  },
  {
    id: "settings",
    hash: "#/settings",
    label: "Settings",
    icon: Settings,
    component: lazy(() => import("./screens/settings")),
  },
]

export const DEFAULT_ROUTE: RouteId = "wiki"

function routeFromHash(hash: string): RouteId {
  const clean = hash.replace(/^#\/?/, "").split(/[/?]/)[0]
  // Admin management lives in the Settings screen's admin-only tab; keep
  // old `#/admin` deep links working.
  if (clean === "admin") return "settings"
  const match = ROUTES.find((r) => r.id === clean)
  return match ? match.id : DEFAULT_ROUTE
}

/** Programmatic navigation — screens may also just set location.hash. */
export function navigate(route: RouteId): void {
  window.location.hash = `#/${route}`
}

/** Current route id, kept in sync with the location hash. */
export function useRoute(): [RouteId, (route: RouteId) => void] {
  const [route, setRoute] = useState<RouteId>(() =>
    routeFromHash(window.location.hash),
  )

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash(window.location.hash))
    window.addEventListener("hashchange", onHash)
    return () => window.removeEventListener("hashchange", onHash)
  }, [])

  const go = useCallback((r: RouteId) => navigate(r), [])
  return [route, go]
}
